import moment, {Moment} from 'moment';
import {PeriodParamKey, PeriodTimelineMode} from './period-selector9';
import {DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT} from './period-selector9.constants';

/**
 * The one place the backend param shape is defined, so the selector's live emission
 * ({@link PeriodSelector9Component}'s `period()`) and the table's initial seed
 * ({@link TablePeriodParams.seed}) cannot drift: both hand their resolved endpoints and flags
 * here and get byte-identical params. Endpoints arrive already clamped (whole days for a
 * timeframe) and times already resolved to their defaults.
 */
export interface PeriodParamsInput {
  /**
   * The Timeline/Schedule tab, or `null` when the selector has no switch. Only a switchable
   * selector reports one, so `periodMode` stays absent everywhere the user cannot choose it —
   * a selector with a plain timeframe sends the same params it always did.
   */
  timelineMode: PeriodTimelineMode | null;
  /** Final endpoints — `null` in `all` mode. A timeframe passes whole days here. */
  startDate: Moment | null;
  endDate: Moment | null;
  /** Endpoints carry a time of day (`datetime` granularity, no timeframe active). */
  emitsTime: boolean;
  /** The timeframe is in play: whole dates plus a `startTime`/`endTime` window. */
  usesTimeframe: boolean;
  /** Resolved timeframe (`HH:mm`), defaults already applied. Only emitted when `usesTimeframe`. */
  startTime: string;
  endTime: string;
  /** Drop Saturdays/Sundays from the timeframe. Only emitted when `usesTimeframe`. */
  excludeWeekends: boolean;
}

/** Builds the ready-to-send query params for a resolved selection. See {@link PeriodParamsInput}. */
export function buildPeriodParams(input: PeriodParamsInput): Partial<Record<PeriodParamKey, string>> {
  const params: Partial<Record<PeriodParamKey, string>> = {};
  // Not a bound but the reading of them, so it goes out even in `all` mode, which has none.
  if (input.timelineMode) {
    params.periodMode = input.timelineMode;
  }
  if (input.startDate && input.endDate) {
    const format = input.emitsTime ? DATE_TIME_FORMAT : DATE_FORMAT;
    params.startDate = input.startDate.format(format);
    params.endDate = input.endDate.format(format);
  }
  if (input.usesTimeframe) {
    params.startTime = input.startTime;
    params.endTime = input.endTime;
    // Weekends are only ever filtered out, so that an absent flag reads the same as a
    // selector with no toggle at all.
    if (input.excludeWeekends) params.excludeWeekends = 'true';
  }
  return params;
}

/**
 * Whether the timeframe narrows the range (whole dates plus a repeating window) rather than the
 * endpoints standing alone. With a Timeline/Schedule switch it applies only on the Schedule tab;
 * without one, a plain timeframe always applies. The single definition the selector's computed
 * and the table controller's getter both call, so the two cannot drift.
 */
export function computeUsesTimeframe(hasTimelineSwitch: boolean, scheduleTab: boolean, hasTimeframe: boolean): boolean {
  return hasTimelineSwitch ? scheduleTab : hasTimeframe;
}

/**
 * Whether the emitted endpoints carry a time of day: a `datetime` selector always does, unless a
 * timeframe owns the time instead (which sends whole dates plus `startTime`/`endTime`). Shared by
 * the selector and the table. Independent of mode — see the note on the selector's `emitsTime`.
 */
export function computeEmitsTime(datetime: boolean, usesTimeframe: boolean): boolean {
  return datetime && !usesTimeframe;
}

/** The given date at the given `HH:mm`, or an untouched clone if the time does not parse. */
export function applyTime(date: Moment, time: string): Moment {
  const parsed = moment(time, TIME_FORMAT, true);
  return parsed.isValid()
    ? date.clone().hours(parsed.hours()).minutes(parsed.minutes()).seconds(0).milliseconds(0)
    : date.clone();
}
